import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Lock, QrCode, ScanLine, Send, ShieldCheck,
  Rocket, Sparkles, Check, X, ExternalLink,
} from "lucide-react";
import { BrandLogo } from "@/components/ui/brand-logo";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

const attachItems = [
  "🚗 Your Car Windows", "🐶 Your Pet", "🧳 Your Luggage", "🔑 Your Keys",
  "🎒 Your Backpack", "🚲 Your Bicycle", "👶 Children's School Bags",
  "👵 Seniors' Wristbands", "🛠 Tools & Equipment", "🏢 Business Assets",
];

const steps = [
  { icon: QrCode, title: "Attach", desc: "Attach a ReachMeNow QR tag to your important belongings." },
  { icon: ScanLine, title: "Scan", desc: "Someone scans your QR code using any smartphone camera." },
  { icon: Send, title: "Message", desc: "They send you a private message through ReachMeNow." },
  { icon: Lock, title: "Reply Privately", desc: "You receive the message instantly and reply securely — without revealing your personal contact information." },
];

const traditional = [
  "Phone number is publicly visible",
  "Can't update your contact information easily",
  "Spam calls and unwanted messages",
  "No conversation history",
  "Limited to one purpose",
];

const rmn = [
  "Your personal information stays private",
  "Secure QR-based messaging",
  "Update your information anytime",
  "Works anywhere with an internet connection",
  "One account for multiple items",
  "Modern, secure, and easy to use",
];

const perfectFor = [
  "🚗 Cars", "🐶 Pets", "🧳 Luggage", "🔑 Keys", "🎒 School Bags", "💻 Laptop Bags",
  "🚲 Bikes & Scooters", "👶 Children", "👵 Seniors", "🏢 Businesses", "📦 Company Equipment", "🏕 Outdoor Gear",
];

const demoSteps = [
  "Register for free", "Generate a demo QR code", "Scan it using another smartphone",
  "Send a private message", "Experience the platform exactly as it works today",
];

const built = ["Working website", "Live demonstration", "QR generation", "Secure messaging"];

const help = [
  "Release the app on Play Store and App Store",
  "Manufacture our first production run",
  "Produce premium QR stickers and smart contact tags (backers can choose their reward)",
  "Improve our web platform",
  "Expand into new product categories",
  "Improve security and reliability",
  "Launch internationally",
];

const Overline = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">{children}</span>
);

export default function AboutUs() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#fafafa] font-body flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-brand-navy/95 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><BrandLogo dark /></Link>
          <Link to="/" className="text-sm text-slate-200 hover:text-white flex items-center gap-1.5" data-testid="about-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1" data-testid="about-page">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="absolute top-40 -left-40 h-96 w-96 rounded-full bg-orange-200/40 blur-3xl" />
          <div className="relative max-w-3xl mx-auto px-5 pt-16 pb-10">
            <Overline>About ReachMeNow</Overline>
            <h1 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Stay <span className="text-brand-blue">Reachable.</span> Stay <span className="text-brand-orange">Private.</span>
            </h1>
            <div className="mt-6 space-y-4 text-lg text-slate-600 leading-relaxed">
              <p>Imagine returning to your parked car only to discover someone wanted to tell you your headlights were left on. Imagine losing your luggage at the airport, misplacing your keys, or having your pet wander away. Every day, people find lost or unattended items and genuinely want to help — but they often have no safe way to contact the owner without exposing personal information.</p>
              <p>Most people solve this by displaying their phone number on a car dashboard, luggage tag, pet collar, or keychain. Unfortunately, that also exposes them to spam calls, unwanted messages, and privacy risks.</p>
              <p className="font-heading font-semibold text-brand-navy">There has to be a better way.</p>
            </div>
          </div>
        </section>

        {/* Introducing */}
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-3xl mx-auto px-5 py-16">
            <Overline>Introducing ReachMeNow</Overline>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">A privacy-first smart QR contact platform</h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-lg">
              ReachMeNow allows anyone to reach you without ever seeing your phone number or email address. Simply attach a ReachMeNow smart QR tag to anything important:
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {attachItems.map((t) => (
                <div key={t} className="bg-[#fafafa] border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700">{t}</div>
              ))}
            </div>
            <p className="mt-8 text-slate-600 leading-relaxed text-lg">
              When someone scans your QR code with their smartphone, they're taken to a secure messaging page where they can send you a message instantly. Your phone number and email remain completely private.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">How it works</span>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Secure communication in four steps</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center text-white shadow-lg shrink-0">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="font-heading font-extrabold text-2xl text-slate-200">0{i + 1}</span>
                </div>
                <h3 className="mt-4 font-heading font-bold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <p className="font-heading font-bold text-xl text-brand-navy">No phone numbers. No email addresses.</p>
            <p className="text-slate-500 mt-1">Just secure, private communication when it matters most.</p>
          </div>
        </section>

        {/* Why */}
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-5xl mx-auto px-5 py-16">
            <div className="text-center max-w-2xl mx-auto">
              <Overline>Why ReachMeNow?</Overline>
              <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Complete control over your privacy</h2>
            </div>
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              <div className="bg-[#fafafa] border border-slate-200 rounded-2xl p-7">
                <h3 className="font-heading font-bold text-lg text-slate-700">Traditional Contact Labels</h3>
                <ul className="mt-5 space-y-3">
                  {traditional.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-600">
                      <X className="h-4 w-4 text-red-400 mt-0.5 shrink-0" /> <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-orange-50 border border-brand-blue/20 rounded-2xl p-7">
                <h3 className="font-heading font-bold text-lg text-brand-blue">ReachMeNow</h3>
                <ul className="mt-5 space-y-3">
                  {rmn.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /> <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Perfect for */}
        <section className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">Perfect for</span>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">A platform that protects almost anything</h2>
            <p className="mt-3 text-slate-500">ReachMeNow isn't just one product — it's a platform that can protect almost anything that matters to you.</p>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {perfectFor.map((t) => (
              <div key={t} className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium text-slate-700 text-center shadow-sm">{t}</div>
            ))}
          </div>
          <p className="mt-8 max-w-3xl mx-auto text-center text-slate-500 leading-relaxed">
            As we grow, we'll continue expanding into new products and accessories while keeping the same simple idea: help people reach you without exposing your personal information.
          </p>
        </section>

        {/* Live demo CTA */}
        <section className="max-w-6xl mx-auto px-5 py-8">
          <div className="relative overflow-hidden rounded-3xl bg-brand-navy text-white p-10 md:p-14">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-orange/30 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-blue/40 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange bg-white/10 px-3 py-1.5 rounded-full">
                <Sparkles className="h-3.5 w-3.5" /> Try the Live Demo Today
              </span>
              <h2 className="mt-6 font-heading font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">A working demonstration — not just an idea</h2>
              <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">
                Unlike many crowdfunding campaigns that showcase only ideas or prototypes, ReachMeNow already has a working demonstration. With the demo you can:
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2.5 max-w-2xl">
                {demoSteps.map((t) => (
                  <li key={t} className="flex items-center gap-2.5 text-slate-200"><Check className="h-4 w-4 text-brand-orange shrink-0" /> {t}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="bg-brand-blue hover:bg-brand-bluehover text-white rounded-full px-7 h-12" onClick={() => navigate("/demo")} data-testid="about-try-demo-btn">
                  Try the Free Demo <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-white/30 bg-white/5 text-white hover:bg-white/15" asChild data-testid="about-visit-site-btn">
                  <a href="https://reachmenow.ca" target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-1.5 h-4 w-4" /> Visit reachmenow.ca</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why support */}
        <section className="max-w-5xl mx-auto px-5 py-16">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <Overline>Why we need your support</Overline>
              <h2 className="mt-3 font-heading font-bold text-3xl tracking-tight">We've already built the foundation</h2>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {built.map((t) => (
                  <div key={t} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700">
                    <Check className="h-4 w-4 text-green-500 shrink-0" /> {t}
                  </div>
                ))}
              </div>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Now we need your support to take ReachMeNow from a working prototype to a real consumer product.
              </p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-brand-navy">Your contribution will help us:</h3>
              <ul className="mt-4 space-y-3">
                {help.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-600">
                    <span className="h-6 w-6 rounded-lg bg-orange-50 flex items-center justify-center shrink-0 mt-0.5"><Rocket className="h-3.5 w-3.5 text-brand-orange" /></span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-10 text-center max-w-3xl mx-auto text-slate-500 leading-relaxed">
            Every contribution helps bring ReachMeNow closer to becoming the world's easiest and safest way for people to stay reachable while protecting their privacy.
          </p>
        </section>

        {/* Vision */}
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-3xl mx-auto px-5 py-16 text-center">
            <Overline>Our Vision</Overline>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Privacy shouldn't come at the cost of convenience</h2>
            <div className="mt-6 space-y-4 text-lg text-slate-600 leading-relaxed">
              <p>Our vision is to create a complete ecosystem of privacy-first smart contact products that help people reconnect with their belongings, loved ones, and communities without sharing sensitive personal information.</p>
              <p>Today it starts with cars, pets, luggage, and keys. Tomorrow it expands to families, businesses, schools, travel, healthcare, and countless everyday situations where secure communication matters.</p>
              <p>Thank you for taking the time to learn about ReachMeNow. Whether you become a backer, share our campaign with friends, or simply help spread the word, you're helping us build a future where staying reachable doesn't mean giving up your privacy.</p>
            </div>
            <p className="mt-8 font-heading font-extrabold text-2xl tracking-tight">
              Stay <span className="text-brand-blue">Reachable.</span> Stay <span className="text-brand-orange">Private.</span>
            </p>
            <div className="mt-8">
              <Button size="lg" className="bg-brand-orange hover:bg-brand-orangehover text-white rounded-full px-7 h-12" onClick={() => navigate("/demo")} data-testid="about-cta-btn">
                <ShieldCheck className="mr-2 h-4 w-4" /> Try the Live Demo
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
